import React from "react";
import {
  Box,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import {
  MODULE_DETAILS,
  MODULE_PRICING,
  isModuleAvailable,
} from "../constants/moduleSubscriptionCatalog";
import PublicMarketingShell from "../components/PublicMarketingShell";
import {
  PM_COLORS,
  publicCardSx,
  publicHeroChipSx,
  publicSubtitleSx,
  publicTitleSx,
} from "../constants/publicMarketingTheme";

const CORE_COLOR = "#94a3b8";

/** Cross-module links reflected in product behavior and catalog copy. */
const INTEGRATION_FLOWS = [
  {
    from: "Core",
    fromLabel: "Core",
    to: "Procurement",
    toLabel: "Procurement",
    detail:
      "Shared supplier master drives purchase requests, purchase orders, and supplier quote workflows.",
  },
  {
    from: "Core",
    fromLabel: "Core",
    to: "Inventory",
    toLabel: "Inventory",
    detail:
      "Products from Core inventory catalog feed stock rows, transactions, and adjustments.",
  },
  {
    from: "Core",
    fromLabel: "Core",
    to: "Sales",
    toLabel: "Sales",
    detail:
      "Customers and catalog items underpin opportunities, quotations, and sales orders.",
  },
  {
    from: "Procurement",
    fromLabel: "Procurement",
    to: "Inventory",
    toLabel: "Inventory",
    detail:
      "When both modules are on: purchase request lines and goods receipts align with warehouses and stock.",
  },
  {
    from: "Procurement",
    fromLabel: "Procurement",
    to: "Accounting",
    toLabel: "Accounting",
    detail:
      "Purchase-side activity ties to vendor bills and automated journal treatment where Accounting is enabled.",
  },
  {
    from: "Sales",
    fromLabel: "Sales",
    to: "Inventory",
    toLabel: "Inventory",
    detail:
      "Sales order fulfillment can consume stock and post inventory movements when Inventory is subscribed.",
  },
  {
    from: "Sales",
    fromLabel: "Sales",
    to: "Accounting",
    toLabel: "Accounting",
    detail:
      "Customer invoices and related journal entries flow from sales activity when Accounting is on.",
  },
  {
    from: "Inventory",
    fromLabel: "Inventory",
    to: "Accounting",
    toLabel: "Accounting",
    detail:
      "Inventory-related postings (e.g. from receipts or fulfillment) can reach the GL when integrated.",
  },
  {
    from: "HR",
    fromLabel: "HR",
    to: "Accounting",
    toLabel: "Accounting",
    detail:
      "Payroll runs and transactions can post through to Accounting where that integration path is configured.",
  },
  {
    from: "Marketing",
    fromLabel: "Marketing",
    to: "Sales",
    toLabel: "Sales",
    detail:
      "Leads and campaigns connect forward into opportunities and quotations as deals progress.",
  },
];

function moduleColor(key) {
  if (key === "Core") return CORE_COLOR;
  return MODULE_PRICING.find((m) => m.key === key)?.color ?? "#64748b";
}

function ModuleChip({ label, colorKey }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 700,
        borderLeft: `4px solid ${moduleColor(colorKey)}`,
        bgcolor: "rgba(15,23,42,0.55)",
        color: "#f1f5f9",
      }}
    />
  );
}

const cardPaperSx = {
  ...publicCardSx,
  p: 2.25,
};

const ModuleIntegrationsPage = () => {
  return (
    <PublicMarketingShell>
      <Container maxWidth="lg">
        <Chip label="How modules connect" sx={publicHeroChipSx} />
        <Typography
          variant="h3"
          sx={{ ...publicTitleSx, mb: 1.5, lineHeight: 1.15 }}
        >
          Module integrations & data flow
        </Typography>
        <Typography
          variant="body1"
          sx={{
            ...publicSubtitleSx,
            maxWidth: 800,
            mb: 3,
            animation: "fadeUp 500ms ease-out",
          }}
        >
          This map matches your subscription catalog: each arrow is a real hand-off
          between areas of the product. Subscriptions still gate which screens you
          see—only active modules run end-to-end together.
        </Typography>

        <Paper
          sx={{
            ...cardPaperSx,
            mb: 3,
            animation: "fadeUp 550ms ease-out",
            borderStyle: "dashed",
            borderColor: "rgba(148,163,184,0.45)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <HubOutlinedIcon sx={{ color: CORE_COLOR }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: PM_COLORS.textPrimary }}>
              Core (shared master data)
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ opacity: 0.92, mb: 1.5 }}>
            Organizations, users, roles, and shared masters—suppliers, customers,
            and products—sit in Core and are reused by Procurement, Inventory, and
            Sales so you do not duplicate reference data across modules.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
            <Chip label="Suppliers" size="small" variant="outlined" sx={{ color: "#e2e8f0" }} />
            <Chip label="Customers" size="small" variant="outlined" sx={{ color: "#e2e8f0" }} />
            <Chip label="Products" size="small" variant="outlined" sx={{ color: "#e2e8f0" }} />
            <Chip label="Users & RBAC" size="small" variant="outlined" sx={{ color: "#e2e8f0" }} />
          </Stack>
        </Paper>

        <Typography variant="h5" sx={{ ...publicTitleSx, mb: 2, fontSize: "1.35rem" }}>
          Connection map
        </Typography>
        <Stack spacing={1.75} sx={{ animation: "fadeUp 650ms ease-out" }}>
          {INTEGRATION_FLOWS.map((row, idx) => (
            <Paper key={`flow-${idx}`} sx={cardPaperSx}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1.5}
              >
                <ModuleChip label={row.fromLabel} colorKey={row.from} />
                <ArrowForwardIcon
                  sx={{
                    color: "rgba(148,163,184,0.9)",
                    alignSelf: { xs: "center", sm: "center" },
                  }}
                />
                <ModuleChip label={row.toLabel} colorKey={row.to} />
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.92,
                    flex: 1,
                    minWidth: 0,
                    pl: { xs: 0, sm: 1 },
                  }}
                >
                  {row.detail}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Divider sx={{ my: 4, borderColor: "rgba(148,163,184,0.25)" }} />

        <Typography variant="h5" sx={{ ...publicTitleSx, mb: 2, fontSize: "1.35rem" }}>
          What each module exposes (from your catalog)
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            animation: "fadeUp 750ms ease-out",
          }}
        >
          {MODULE_PRICING.map((m) => {
            const details = MODULE_DETAILS[m.key];
            const on = isModuleAvailable(m);
            return (
              <Box key={m.key} sx={{ flex: "1 1 340px", minWidth: 0 }}>
                <Paper sx={{ ...cardPaperSx, height: "100%" }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: m.color,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>
                      {m.label}
                    </Typography>
                    {!on ? (
                      <Chip label="Roadmap" size="small" color="default" variant="outlined" />
                    ) : null}
                  </Stack>
                  {details?.sections?.length ? (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ color: "#93c5fd", display: "block", mb: 0.5 }}
                      >
                        Main areas
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.88, mb: 1.25 }}>
                        {details.sections.slice(0, 6).join(" · ")}
                        {details.sections.length > 6 ? " · …" : ""}
                      </Typography>
                    </>
                  ) : null}
                  {details?.functionalities?.length ? (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ color: "#86efac", display: "block", mb: 0.5 }}
                      >
                        Integrations & behavior
                      </Typography>
                      <Stack component="ul" sx={{ m: 0, pl: 2.25, opacity: 0.9 }}>
                        {details.functionalities.map((line, i) => (
                          <Typography
                            key={`${m.key}-fn-${i}`}
                            component="li"
                            variant="body2"
                            sx={{ mb: 0.35 }}
                          >
                            {line}
                          </Typography>
                        ))}
                      </Stack>
                    </>
                  ) : null}
                </Paper>
              </Box>
            );
          })}
        </Box>

        <Paper
          sx={{
            ...cardPaperSx,
            mt: 3,
            p: 2.5,
            animation: "fadeUp 850ms ease-out",
            bgcolor: "rgba(11, 18, 32, 0.72)",
          }}
        >
          <Typography variant="subtitle2" sx={{ color: PM_COLORS.cyan, mb: 0.5 }}>
            Subscription reminder
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.92 }}>
            Edges above only apply when both sides are in your plan (for example,
            Procurement + Inventory for goods receipts, or Sales + Accounting for
            invoicing). Roadmap modules ({MODULE_PRICING.filter((x) => !isModuleAvailable(x)).map((x) => x.label).join(", ")}) preview future scope in{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              moduleSubscriptionCatalog.js
            </Box>{" "}
            —same source as Sign up and Pricing.
          </Typography>
        </Paper>
      </Container>
    </PublicMarketingShell>
  );
};

export default ModuleIntegrationsPage;
